import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import { type FieldInfo, readFields } from "@/office/word/layout";

const KEINE_FELDER: CheckResult = unsupported(
  "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4).",
  "Mit Alt + F9 zeigen sich Einträge als { XE \"…\" } und das Verzeichnis als { INDEX }. " +
    "Danach kannst du den Schritt selbst als erledigt markieren.",
);

function eintraege(felder: FieldInfo[]): string[] {
  return felder
    .filter((feld) => /^XE\b/i.test(feld.code))
    .map((feld) => /^XE\s+"([^"]*)"/i.exec(feld.code)?.[1] ?? "")
    .filter((text) => text.length > 0);
}

export const lektionStichwortverzeichnis: Lesson = {
  id: "stichwortverzeichnis",
  title: "Das Stichwortverzeichnis (Index)",
  summary: "Begriffe markieren, und Word listet am Ende, auf welchen Seiten sie vorkommen.",
  durationMinutes: 9,
  learningGoals: [
    "Indexeinträge mit „Eintrag markieren“ setzen",
    "Ein Stichwortverzeichnis einfügen und aktualisieren",
    "Untereinträge und Querverweise im Index nutzen",
  ],
  steps: [
    {
      id: "eintraege-markieren",
      title: "Einträge markieren",
      explanation: [
        "Ein Stichwortverzeichnis – im Menü „Index“ genannt – entsteht in zwei Schritten. " +
          "Zuerst werden die Begriffe markiert: Wort markieren, „Verweise“ → „Eintrag " +
          "markieren“ (Alt + Umschalt + X). Der Dialog übernimmt das Wort; „Alle markieren“ " +
          "setzt einen Eintrag an jeder Fundstelle.",
        "Im selben Dialog lassen sich Untereinträge anlegen („Tabelle“ → „Formatvorlage“) " +
          "und Querverweise („Tabellenvorlage: siehe Formatvorlage“).",
        "Die Einträge sind versteckte XE-Felder. Word blendet dazu die Formatierungszeichen " +
          "ein; mit Strg + Umschalt + + (oder ¶ auf „Start“) verschwinden sie wieder.",
      ],
      keyPoints: [
        "Verweise → Eintrag markieren (Alt + Umschalt + X).",
        "„Alle markieren“ erfasst jede Fundstelle.",
        "Einträge sind versteckte XE-Felder.",
      ],
      task: {
        instruction: "Markiere mindestens drei verschiedene Begriffe als Indexeinträge.",
        hint: "Begriff markieren, Alt + Umschalt + X, „Alle markieren“, nächsten Begriff wählen – der Dialog bleibt offen.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) return KEINE_FELDER;
          const begriffe = new Set(eintraege(felder).map((text) => text.toLowerCase()));
          return fromCriteria(
            [criterion(`Mindestens drei verschiedene Indexeinträge (gefunden: ${begriffe.size})`, begriffe.size >= 3)],
            {
              success: "Die Begriffe sind markiert – jetzt fehlt nur noch das Verzeichnis.",
              failure: "Es sind noch nicht genug Begriffe markiert.",
            },
          );
        },
      },
    },
    {
      id: "index-einfuegen",
      title: "Das Verzeichnis einfügen",
      explanation: [
        "Am Ende des Dokuments: „Verweise“ → „Index einfügen“. Der Dialog legt Format, " +
          "Spaltenzahl und die Ausrichtung der Seitenzahlen fest. Word sortiert die Begriffe " +
          "alphabetisch und fügt Buchstabenüberschriften ein.",
        "Wie jedes Verzeichnis ist der Index ein Feld. Nach Änderungen am Text markierst du " +
          "ihn und drückst F9 – oder „Index aktualisieren“ im Menüband.",
      ],
      keyPoints: [
        "Verweise → Index einfügen.",
        "F9 aktualisiert den Index.",
      ],
      task: {
        instruction: "Füge am Ende des Dokuments ein Stichwortverzeichnis ein.",
        hint: "Cursor ans Dokumentende, Verweise → Index einfügen → OK.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) return KEINE_FELDER;
          return fromCriteria(
            [
              criterion("Indexeinträge vorhanden", eintraege(felder).length > 0),
              criterion("Ein Stichwortverzeichnis ist eingefügt", felder.some((feld) => /^INDEX\b/i.test(feld.code))),
            ],
            {
              success: "Das Stichwortverzeichnis steht und nennt zu jedem Begriff die Seiten.",
              failure: "Das Verzeichnis fehlt noch.",
            },
          );
        },
      },
    },
  ],
};
